import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { BrowserFrame } from "./BrowserFrame";
import { cn } from "@/lib/utils";
import { LayoutDashboard, BarChart3, ListChecks, Sparkles, Building2, FileBarChart2, ScrollText, Maximize2, PlayCircle } from "lucide-react";

export const SCREENS = [
  { key: "dashboard", icon: LayoutDashboard, title: "Your command centre", body: "Paid this month, pending reviews, exception rate and every payment deadline — one glance, every morning.", src: "/screens/dashboard.png" },
  { key: "invoice", icon: Sparkles, title: "Every line coded with a confidence score", body: "The AI explains each GL suggestion. Override with one click — the change is logged and remembered for that supplier.", src: "/screens/invoice.png" },
  { key: "review", icon: ListChecks, title: "Review only what needs a human", body: "Low-confidence, duplicate and over-limit invoices land in a single queue with 48-hour SLAs and escalation.", src: "/screens/review.png" },
  { key: "spend", icon: BarChart3, title: "Spend trends by GL category", body: "Six months of coded spend, top categories and the live invoice register — no spreadsheet exports.", src: "/screens/spend.png" },
  { key: "suppliers", icon: Building2, title: "Supplier intelligence", body: "Learned GL codes, flag rates, statement reconciliation and missing-invoice chases for every supplier.", src: "/screens/suppliers.png" },
  { key: "reports", icon: FileBarChart2, title: "Reports & close packs", body: "Weekly, monthly and fiscal-year reports as PDF or Excel, filtered by status, GL code or reviewer.", src: "/screens/reports.png" },
  { key: "audit", icon: ScrollText, title: "An audit trail auditors love", body: "Who approved what, when and why — immutable, searchable and linked back to every invoice.", src: "/screens/audit.png" },
];

const ScreenTab = ({ s, active, onClick, index }) => (
  <button data-testid={`showcase-tab-${s.key}`} onClick={onClick} className={cn("group w-full rounded-xl border p-4 text-left transition-colors", active ? "border-primary/60 bg-primary/10" : "border-white/10 hover:border-white/25")}>
    <div className="flex items-center gap-3">
      <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors", active ? "bg-primary text-white" : "bg-white/10 text-white/70 group-hover:bg-white/15")}><s.icon className="h-4 w-4" /></span>
      <span className="font-semibold leading-tight">{s.title}</span>
    </div>
    <AnimatePresence initial={false}>
      {active && <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden text-sm text-white/60"><span className="block pt-2">{s.body}</span></motion.p>}
    </AnimatePresence>
  </button>
);

export const ProductShowcase = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [zoom, setZoom] = useState(null);
  const s = SCREENS[active];

  useEffect(() => {
    if (paused || zoom) return;
    const t = setInterval(() => setActive((a) => (a + 1) % SCREENS.length), 6000);
    return () => clearInterval(t);
  }, [paused, zoom]);

  return (
    <section id="product" className="border-t border-white/10" data-testid="product-showcase">
      <div className="mx-auto max-w-6xl px-6 py-20">
        <h2 className="text-base md:text-lg font-semibold text-primary">Inside D20</h2>
        <p className="mt-2 font-heading text-3xl sm:text-4xl font-bold tracking-tight">The real product — not a mock-up</p>
        <p className="mt-3 max-w-2xl text-white/60">Every screen below is D20 running on live demo data. Click any image to enlarge it.</p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_1.6fr] items-start" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="space-y-2" data-testid="showcase-tabs">
            {SCREENS.map((sc, i) => <ScreenTab key={sc.key} s={sc} index={i} active={i === active} onClick={() => setActive(i)} />)}
          </div>
          <div className="relative lg:sticky lg:top-24">
            <div className="absolute -inset-4 rounded-3xl bg-primary/15 blur-3xl" />
            <BrowserFrame className="relative cursor-zoom-in" data-testid="showcase-frame" onClick={() => setZoom(s)}>
              <div className="relative aspect-[16/10] overflow-hidden bg-[#07080d]">
                <AnimatePresence mode="wait">
                  <motion.img key={s.key} src={s.src} alt={s.title} loading="lazy" data-testid="showcase-image" initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }} className="absolute inset-0 h-full w-full object-cover object-top" />
                </AnimatePresence>
                <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[11px] text-white/80 backdrop-blur"><Maximize2 className="h-3 w-3" /> Enlarge</span>
              </div>
            </BrowserFrame>
            <div className="mt-3 flex justify-center gap-1.5">
              {SCREENS.map((sc, i) => <button key={sc.key} aria-label={sc.title} data-testid={`showcase-dot-${sc.key}`} onClick={() => setActive(i)} className={cn("h-1.5 rounded-full transition-all", i === active ? "w-6 bg-primary" : "w-1.5 bg-white/25 hover:bg-white/50")} />)}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-[min(96vw,1400px)] border-white/10 bg-[#0b0d14] p-2 sm:p-3" data-testid="showcase-lightbox">
          {zoom && <>
            <img src={zoom.src} alt={zoom.title} className="w-full rounded-lg" />
            <div className="px-2 pb-1 pt-2"><div className="font-semibold text-white">{zoom.title}</div><div className="text-sm text-white/60">{zoom.body}</div></div>
          </>}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export const TourVideo = () => (
  <div className="relative" data-testid="tour-video-wrap">
    <div className="absolute -inset-4 rounded-3xl bg-primary/15 blur-3xl" />
    <BrowserFrame className="relative" title="d20 · 2-minute walkthrough">
      <video data-testid="tour-video" className="block w-full aspect-video object-cover bg-[#07080d]" src="/screens/d20-freshbooks-tour.mp4" poster="/screens/tour-poster.jpg" autoPlay muted loop playsInline controls />
    </BrowserFrame>
    <div className="mt-3 flex items-center justify-center gap-2 text-xs text-white/50"><PlayCircle className="h-3.5 w-3.5" /> Recorded live, inbox to ledger — unmute for sound.</div>
  </div>
);
